/* Rhody Reviews api/v1 -- core domain. PURE EXTRACTION from lib/api.ts (gate-prep split); no behavior change. */
import { request, OAUTH_BASE } from './_http';
import type { GoogleConnectionStatus } from './google';

/* Period-over-period comparison block (additive, 2026-06-30). Backend
   GET /analytics/summary now returns this alongside the existing series.
   Optional so any surface that ignores it keeps working unchanged. A null
   `previous`/`delta` means no prior data; a null `pct_change` means the prior
   baseline was 0 (show the absolute delta, not a percent). `dir` maps 1:1
   onto the Kpi `dir` prop. */
export type ComparisonDir = 'up' | 'down' | 'flat';
export interface ComparisonMetric {
  current: number | null;
  previous: number | null;
  delta: number | null;
  pct_change: number | null;
  dir: ComparisonDir;
}
export interface AnalyticsComparison {
  window_days: number;
  current: { start: string; end: string };
  previous: { start: string; end: string };
  as_of: string;
  metrics: {
    total_reviews: ComparisonMetric;
    avg_rating: ComparisonMetric;
    reply_rate: ComparisonMetric; // 0..1 fraction
  };
}

export interface AnalyticsSummary {
  client_id: string;
  review_count_by_week: unknown;
  avg_rating_trend: unknown;
  response_rate_by_month: unknown;
  as_of: string;
  /* Additive: present only on responses from the 2026-06-30 backend onward. */
  comparison?: AnalyticsComparison;
}

export interface HealthResponse {
  status: string;
  version?: string;
}

/* Returned by POST /api/v1/auth/session-token (engine/api/v1/session_token.py).
   The bridge from a Supabase login to a data-plane rr_ token. */
export interface SessionTokenResponse {
  api_key: string;
  key_prefix: string;
  client_id: string;
  business_name: string;
  status: string;
}

/* GET /me -- the caller's client context (so googleConnectUrl(clientId)
   etc. can fire without guessing the id). */
export interface MeResponse {
  client_id: string;
  business_name: string;
  status: string;
  tier: string;
  /* Google connect state (P2-1): whether OAuth is done (a refresh token is
     stored) and whether we have a location/place to actually pull reviews
     from. The Integrations + Onboarding screens use these to show a REAL
     connected status and to decide if the first import can run. Optional so
     older API responses (pre-2026-06-26) don't break typing. */
  google_connected?: boolean;
  google_location_known?: boolean;
  /* CANONICAL Google connection state (2026-07-07 onboarding rebuild). This
     is the SINGLE SOURCE OF TRUTH for every "Connected" render AND the
     first-real-send gate. `connected` ONLY when oauth_token_valid &&
     listing_confirmed && reviews_imported >= 1. The legacy
     google_connected / google_location_known fields above are token/location
     signals ONLY and MUST NOT drive a "Connected" render anymore. Optional so
     pre-2026-07-07 API responses don't break typing. */
  google_connection_status?: GoogleConnectionStatus;
  oauth_token_valid?: boolean;
  listing_confirmed?: boolean;
  reviews_imported?: number;
  confirmed_place_id?: string | null;
  confirmed_at?: string | null;
  candidate_place_id?: string | null;
  /* GBP connection HEALTH (2026-08-03, gbp_health.py sweep). When
     google_needs_reconnect is true the tenant's stored Google refresh token has
     died (owner revoked access, token expired = invalid_grant, or the stored
     ciphertext no longer decrypts) and the dashboard shows a "reconnect your
     Google account" prompt. Fail-closed: absent (pre-migration / pre-first-sweep)
     reads as not-broken. google_health_status / _checked_at are for display.
     Optional so older API responses don't break typing. */
  google_needs_reconnect?: boolean;
  google_health_status?: string | null;
  google_health_checked_at?: string | null;
  /* Trial state (optional; only present once the backend exposes it). The
     Pass C trial countdown bar reads trial_ends_at to drive a LIVE countdown.
     Per the no-fabrication rule the bar HIDES when this is absent -- we never
     invent a deadline. ISO-8601 timestamp of when the free trial expires. */
  trial_ends_at?: string | null;
  /* Demo-tenant marker (2026-07-06 monetization gate). TRUE for the seeded
     public demo login so the subscription gate can un-gate it even if its
     seeded subscription lapses. Set server-side by _is_demo_tenant in
     engine/api/v1/core.py. Optional so older responses don't break typing. */
  is_demo_tenant?: boolean;
}

export const coreApi = {
  health(signal?: AbortSignal): Promise<HealthResponse> {
    return request<HealthResponse>('/api/v1/health', { token: null, signal });
  },

  analyticsSummary(signal?: AbortSignal): Promise<AnalyticsSummary> {
    return request<AnalyticsSummary>('/api/v1/analytics/summary', { signal });
  },

  /* Exchange a Supabase user JWT (the access_token from the Supabase
     session) for a data-plane rr_ token. The supabaseJwt is sent as the
     Bearer here -- it OVERRIDES any stored rr_ token for this one call. */
  exchangeSessionToken(
    supabaseJwt: string,
    signal?: AbortSignal,
  ): Promise<SessionTokenResponse> {
    return request<SessionTokenResponse>('/api/v1/auth/session-token', {
      method: 'POST',
      token: supabaseJwt,
      signal,
    });
  },

  /* ----- Identity ----- */
  me(signal?: AbortSignal): Promise<MeResponse> {
    return request<MeResponse>('/api/v1/me', { signal });
  },
};

// -------------------------------------------------------------------
// Google connect (REAL): GBP refresh-token capture flow.
// engine/signup/oauth_flow.py -> GET /auth/google/start?client_id=...
// NOTE: this is NOT a login. It connects an existing client's Google
// Business Profile. It requires a known client_id, so it cannot create
// a brand-new session on its own.
// -------------------------------------------------------------------
//
// SECURITY (2026-10-07): /auth/google/start no longer trusts a bare
// client_id. It also needs `t`, a short-lived signed start token that only
// the rr_-authenticated GET /api/v1/auth/google/start-token mints for the
// caller's OWN tenant. Use startGoogleConnect(clientId) from click handlers.
export function googleConnectUrl(clientId: string, startToken: string): string {
  const base = OAUTH_BASE || window.location.origin;
  const u = new URL('/auth/google/start', base + '/');
  u.searchParams.set('client_id', clientId);
  u.searchParams.set('t', startToken);
  return u.toString();
}

export interface GoogleStartTokenResponse {
  client_id: string;
  token: string;
  expires_in: number;
}

/* Fetch a fresh start token, then navigate to Google's consent flow. The
   token is bound to the tenant the rr_ key belongs to, so the server's
   client_id wins over the caller's argument. */
export async function startGoogleConnect(clientId: string): Promise<void> {
  const res = await request<GoogleStartTokenResponse>(
    '/api/v1/auth/google/start-token',
  );
  window.location.href = googleConnectUrl(res.client_id || clientId, res.token);
}
